import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import type { BugEscapeTimingPoint, EscapeTimingBin } from '../../../types/api.js';

/**
 * 90日窓の内側にあたるビン（|起票日 − 起因案件リリース日| ≤ 90日）。
 * 日付基準は現行KPIの90日窓判定（kpi_daily_table の bug_count_90d: 起票日と窓判定用リリース日）と同一。
 * リリース前起票（負値）は KPI 側でも両日付が同じ90日区間に収まれば計上されるため、1〜90日前（beforeRelease）のみ窓内。
 * 91日以上前（beforeRelease91plus）は KPI・バグ内訳（KPI_WINDOW_SQL）と同じく窓外として扱う
 * @graph-connects none
 */
const INSIDE_WINDOW_BINS: ReadonlySet<EscapeTimingBin> = new Set([
  'beforeRelease',
  'd0to30',
  'd31to60',
  'd61to90',
]);

/** 窓内ビンの棒色（KPIが捕捉できる領域） @graph-connects none */
const INSIDE_COLOR = 'var(--color-accent-primary)';
/** 窓外ビンの棒色（KPIの90日窓が見逃す領域） @graph-connects none */
const OUTSIDE_COLOR = 'var(--color-error)';

export interface EscapeTimingChartProps {
  /** ビン別件数（日数昇順・ゼロ埋め済み） */
  data: BugEscapeTimingPoint[];
  /** 取得月数（説明文用） */
  months: number;
  /** 読み込み状態 */
  isLoading: boolean;
  /** ビンクリック時のコールバック。ドリルダウンモーダル表示用 */
  onBinClick?: (bin: EscapeTimingBin) => void;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 信頼性ページの「エスケープ・タイミング」チャート。バグを「起票日−起因案件リリース日」（KPI と同じ日付基準）の日数ビンで棒グラフ表示し、90日窓の内（藍）/外（赤）を色分けして現行KPIが見逃す量と窓外率を可視化する。判定基準を注記し、ビンクリックでドリルダウンモーダルを開く
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects BarChart [calls] 日数ビン棒グラフ描画
 */
export function EscapeTimingChart({ data, months, isLoading, onBinClick }: EscapeTimingChartProps) {
  const { t } = useTranslation();

  // 窓内/窓外を別系列に分けて積み上げる（各ビンはどちらか一方のみ値を持つ＝1本の棒として描画され、
  // recharts の Legend / Tooltip に窓内・窓外の色分けがそのまま乗る）
  const chartData = useMemo(
    () =>
      data.map((point) => ({
        bin: point.bin,
        label: t(`reliability.escapeBin.${point.bin}`),
        insideCount: INSIDE_WINDOW_BINS.has(point.bin) ? point.count : 0,
        outsideCount: INSIDE_WINDOW_BINS.has(point.bin) ? 0 : point.count,
      })),
    [data, t],
  );

  const total = data.reduce((sum, point) => sum + point.count, 0);
  const outsideCount = data
    .filter((point) => !INSIDE_WINDOW_BINS.has(point.bin))
    .reduce((sum, point) => sum + point.count, 0);
  const outsideRatePct = total > 0 ? Math.round((outsideCount / total) * 1000) / 10 : null;

  const hasData = total > 0;

  const tooltipStyle = {
    backgroundColor: 'var(--color-bg-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
  };

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('reliability.escape.title')}
        description={t('reliability.escape.description', { months })}
        action={
          outsideRatePct !== null ? (
            <div className="text-right">
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('reliability.escape.outsideRate')}
              </p>
              <p className="text-xl font-bold text-[var(--color-error)] tabular-nums">
                {outsideRatePct}%
              </p>
            </div>
          ) : undefined
        }
      />
      <CardContent>
        {isLoading ? (
          <div className="h-80 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
        ) : hasData ? (
          <div className="h-80" style={onBinClick ? { cursor: 'pointer' } : undefined}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 8, bottom: 5 }}
                onClick={(state) => {
                  // recharts v3 の activeTooltipIndex は number | string（TooltipIndex）で渡るため数値化する
                  const rawIndex = state?.activeTooltipIndex;
                  const index =
                    typeof rawIndex === 'number'
                      ? rawIndex
                      : typeof rawIndex === 'string' && rawIndex !== ''
                        ? Number(rawIndex)
                        : NaN;
                  const point = Number.isInteger(index) ? data[index] : undefined;
                  if (onBinClick && point) {
                    onBinClick(point.bin);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="label" stroke="var(--color-text-muted)" fontSize={11} />
                <YAxis stroke="var(--color-text-muted)" fontSize={11} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelStyle={{ color: 'var(--color-text-primary)' }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="insideCount"
                  stackId="escape"
                  name={t('reliability.escape.insideWindow')}
                  fill={INSIDE_COLOR}
                />
                <Bar
                  dataKey="outsideCount"
                  stackId="escape"
                  name={t('reliability.escape.outsideWindow')}
                  fill={OUTSIDE_COLOR}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg">
            <p className="text-[var(--color-text-muted)]">{t('reliability.escape.empty')}</p>
          </div>
        )}
        {/* 90日窓の判定基準（KPI と同じ起票日ベース）をページ上に明記する */}
        <p className="mt-3 text-xs text-[var(--color-text-muted)]">{t('reliability.windowNote')}</p>
      </CardContent>
    </Card>
  );
}
